import type { PlatformRole } from '@osteopro/shared';

/**
 * Les deux questions que pose la console de plateforme, écrites une seule fois.
 *
 * Le rôle de plateforme est un axe **distinct** du rôle dans un cabinet : il ne donne aucun droit
 * sur les données d'un cabinet, et le rôle de propriétaire n'en donne aucun sur la console. Les
 * confondre était l'erreur de la V1, qui appelait « admin » à la fois le propriétaire d'une
 * organisation et l'administrateur de l'installation.
 *
 * Ces fonctions **n'appliquent rien** : elles décident ce qu'un écran montre. Chaque route du
 * serveur revérifie de son côté (`requirePlatformRole`), et c'est elle qui refuse.
 */

/** Lit la console : `support` et `super_admin`. Le rôle est absent tant que l'identité charge. */
export function isPlatformStaff(role: PlatformRole | undefined): boolean {
  return role === 'support' || role === 'super_admin';
}

/**
 * Écrit dans la console : `super_admin` seul. Poser une offre, suspendre un cabinet, désactiver un
 * compte, nommer un administrateur — quatre gestes que le support ne pose pas. Un écran qui les
 * offrirait à `support` promettrait ce que le serveur refuse.
 */
export function isPlatformAdmin(role: PlatformRole | undefined): boolean {
  return role === 'super_admin';
}

/**
 * Compte de plateforme **sans cabinet** — les administrateurs de l'éditeur. L'application se
 * réduit pour lui à la console et à son propre compte : pas d'agenda, de dossiers, de factures,
 * de mise en route ni de réglages de cabinet, que le serveur lui refuserait de toute façon
 * (`no_active_organization`). Un administrateur qui est aussi titulaire d'un cabinet garde tout.
 * Pendant une séance « Voir comme », c'est le compte regardé qui compte, jamais ce cas-ci.
 */
export function isPlatformOnly(input: {
  platformRole: PlatformRole | undefined;
  organizationCount: number;
  impersonating: boolean;
}): boolean {
  return (
    !input.impersonating && input.organizationCount === 0 && isPlatformStaff(input.platformRole)
  );
}

/** Accueil d'un compte de plateforme sans cabinet. */
export const PLATFORM_HOME_PATH = '/plateforme/apercu';

/** Sections des Paramètres qui ne regardent que le compte : les seules qu'il garde. */
export const PLATFORM_ONLY_SETTINGS = ['profile', 'security'] as const;

/** Ce qu'un compte de plateforme sans cabinet peut ouvrir : la console et son propre compte. */
export function isPlatformOnlyPath(location: string): boolean {
  if (location === '/plateforme' || location.startsWith('/plateforme/')) return true;
  if (location === '/settings') return true;
  return PLATFORM_ONLY_SETTINGS.some((key) => location === `/settings/${key}`);
}
